import { z } from 'zod'
import { errors } from '../error-handling/errors'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/**
 * `public_error_code` — canonical shape is `string`. Ruby callsites also emit
 * symbols (JSON-serialize to string) and, defensively, we coerce numbers to
 * strings so a backend swap to numeric error codes doesn't silently drop.
 */
const PublicErrorCodeSchema = z.union([z.string(), z.number().transform(String)])

/**
 * `error_types` — canonical shape is `string[]`. Some Ruby callsites emit a
 * bare string (or comma-joined string), so accept both and normalize to
 * `string[]`.
 */
const ErrorTypesSchema = z.union([
  z.array(z.string()),
  z.string().transform(s =>
    s
      .split(',')
      .map(v => v.trim())
      .filter(Boolean)
  ),
])

/**
 * Canonical shape of the backend-authored public error payload the mesh
 * Apollo error formatter (see retailer-platform-graphql-mesh
 * `apollo-error-formatter.ts` — `ALLOWED_OUTPUT_META_KEYS`) forwards on the
 * wire. Populated by Pumpkin services via:
 *
 *   raise X.new(msg).with_meta(
 *     public_error_code: "...",
 *     public_error_details: <hash-or-string>,
 *     error_types: ["..."],
 *   )
 *
 * All fields are optional because the backend chooses which to populate.
 * `public_error_details` is `unknown` because the wire shape varies per
 * backend (some send a string, some send a hash); consumers narrow at the
 * callsite via `typeof` or a domain type guard.
 *
 * Composed schema exists to derive `PublicErrorPayload` via `z.infer` (the
 * exported type omits the schema's `unknown` details slot and re-adds it as
 * a generic `TDetails` so consumers can pin the details shape via the
 * `detailsSchema` overload). Runtime validation still happens per-field
 * inside `getPublicErrorPayload` so a single bad field doesn't drop
 * well-shaped siblings.
 */
const PublicErrorPayloadSchema = z.object({
  public_error_code: PublicErrorCodeSchema.optional(),
  public_error_details: z.unknown().optional(),
  error_types: ErrorTypesSchema.optional(),
})

export type PublicErrorPayload<TDetails = unknown> = Omit<
  z.infer<typeof PublicErrorPayloadSchema>,
  'public_error_details'
> & { public_error_details?: TDetails }

const logInvalidField = (field: string, value: unknown, error: z.ZodError) => {
  errors.captureWarning(
    new Error(`Invalid public_error meta.${field} shape`),
    `public_error meta.${field} failed schema validation`,
    { extra: { field, value, zodError: error.flatten() } }
  )
}

/**
 * Extract the canonical public error payload from an Apollo error.
 * Reads only `graphQLErrors[0].extensions.output.meta` — the single formal
 * channel forwarded by the mesh formatter's allowlist.
 *
 * Returns `undefined` when the backend has not populated the channel. Callers
 * should fall back to a generic i18n string in that case rather than reading
 * `error.message` (which the mesh formatter masks to
 * `"An error has occurred."` for internal errors).
 *
 * Validates each field independently against a zod schema. A field with an
 * invalid shape is dropped (not returned) and a warning is logged via Sentry
 * so the backend team gets signal to standardize. Well-shaped sibling fields
 * on the same payload are preserved.
 *
 * Pass an optional `detailsSchema` to validate + type `public_error_details`
 * against a caller-owned zod schema (the wire shape varies per backend, so
 * only the consumer knows what to expect). Without a schema, details is
 * returned as `unknown` and the consumer narrows at the callsite.
 *
 * Does NOT read the legacy `extensions.details` field: services stuffing
 * structured payloads into that field (shoppers `rpc_public_error.rb`,
 * frigate `gateway_error_handler.rb`) should migrate to
 * `with_meta(public_error_details: <hash>)` — the mesh forwards raw objects
 * on `output.meta` verbatim, no `.to_json` needed.
 */
export function getPublicErrorPayload(error: unknown): PublicErrorPayload | undefined
export function getPublicErrorPayload<S extends z.ZodTypeAny>(
  error: unknown,
  detailsSchema: S
): PublicErrorPayload<z.infer<S>> | undefined
export function getPublicErrorPayload<S extends z.ZodTypeAny>(
  error: unknown,
  detailsSchema?: S
): PublicErrorPayload<z.infer<S>> | undefined {
  if (!isRecord(error)) return undefined

  const gqlErrors = error.graphQLErrors
  const firstGraphQLError =
    Array.isArray(gqlErrors) && gqlErrors.length > 0 && isRecord(gqlErrors[0])
      ? gqlErrors[0]
      : error

  const extensions = firstGraphQLError.extensions
  if (!isRecord(extensions)) return undefined

  const output = isRecord(extensions.output) ? extensions.output : undefined
  const meta = output && isRecord(output.meta) ? output.meta : undefined
  if (!meta) return undefined

  const payload: PublicErrorPayload<z.infer<S>> = {}

  if (meta.public_error_code !== undefined && meta.public_error_code !== null) {
    const result = PublicErrorCodeSchema.safeParse(meta.public_error_code)
    if (result.success) payload.public_error_code = result.data
    else logInvalidField('public_error_code', meta.public_error_code, result.error)
  }

  if (meta.public_error_details !== undefined && meta.public_error_details !== null) {
    if (detailsSchema) {
      const result = detailsSchema.safeParse(meta.public_error_details)
      if (result.success) payload.public_error_details = result.data
      else logInvalidField('public_error_details', meta.public_error_details, result.error)
    } else {
      // No schema passed → default overload where z.infer<S> collapses to
      // unknown. TS can't prove that at the impl body (generic erasure),
      // so bridge with a cast.
      payload.public_error_details = meta.public_error_details as z.infer<S>
    }
  }

  if (meta.error_types !== undefined && meta.error_types !== null) {
    const result = ErrorTypesSchema.safeParse(meta.error_types)
    if (result.success) payload.error_types = result.data
    else logInvalidField('error_types', meta.error_types, result.error)
  }

  return Object.keys(payload).length ? payload : undefined
}
